// @ai-generated 2026-09-14T13:05:40+00:00 by malte.ulfers@tngtech.com using Claude Opus 5 (1M context)
import { type ApiContainerActionSnapshot, type ApiOrderSnapshot, type EnumContainerOrderAction } from '@Model';
import { compareForDisplay } from '@synqo/frontend-component-library';
import { type ContainerTypeDetailsByTypeId } from '../Orders/Editing/Containers/useContainerTypeDetails';

/** One named container, going one way. */
export interface PortalIndividualContainerEntry {
    kind: 'individual';
    /** The snapshot row's id: one line is one container. */
    id: string;
    action: EnumContainerOrderAction;
    /** What the type is called in the portal. */
    label: string;
    /** Empty until the operator picks which container of the type it is. */
    containerNumber: string;
    remarks: string;
}

/** The containers of one pooled type going one way, counted rather than named. */
export interface PortalCollectiveContainerEntry {
    kind: 'collective';
    /** Type and direction together: one type going both ways is two lines. */
    id: string;
    action: EnumContainerOrderAction;
    label: string;
    quantity: number;
    /** What the planner noted on the containers behind the line, without repeats. */
    remarks: string[];
}

export type PortalContainerEntry = PortalIndividualContainerEntry | PortalCollectiveContainerEntry;

/** The containers of a requisition, split by whether they are already standing at the site. */
export interface PortalRequisitionContainers {
    booked: PortalContainerEntry[];
    planned: PortalContainerEntry[];
}

/**
 * The containers planned on a requisition's orders, as the lines the portal shows.
 *
 * Follows the ERP's split: a type tracked as named containers gets a line per container, so the
 * customer can tell which one stands where; a pooled type is counted, because its containers are
 * interchangeable and naming one would name a container nobody can point at. Which of the two a
 * type is comes from <paramref name="containerTypes" />, looked up from the master data — the
 * snapshot carries only the link.
 *
 * Booked and planned are kept apart rather than flagged — a container standing at the site and one
 * still to come are different news.
 */
// @ai-generated 2026-09-16T09:02:00+00:00 by malte.ulfers@tngtech.com using Claude Opus 5 (1M context)
export function collectPortalRequisitionContainers(
    snapshots: readonly ApiOrderSnapshot[] | undefined,
    containerTypes: ContainerTypeDetailsByTypeId,
    isPortalSession: boolean,
): PortalRequisitionContainers {
    const booked: ApiContainerActionSnapshot[] = [];
    const planned: ApiContainerActionSnapshot[] = [];

    for (const snapshot of snapshots ?? []) {
        for (const action of snapshot.container_actions ?? []) {
            (action.is_booked ? booked : planned).push(action);
        }
    }

    const nameOf = (action: ApiContainerActionSnapshot) =>
        portalContainerTypeLabel(action, containerTypes, isPortalSession);

    return {
        booked: toEntries(booked, containerTypes, nameOf),
        planned: toEntries(planned, containerTypes, nameOf),
    };
}

/**
 * A type whose record did not arrive gets a line per container rather than a counted one. In the
 * portal that is the likelier case than a failed call: a type without a portal label is filtered out
 * of the customer's master data altogether, so absence says nothing about how it is managed —
 * and counting containers that are individually managed would merge rows the customer can tell
 * apart, while the reverse only splits a line that could have been merged.
 */
function toEntries(
    actions: readonly ApiContainerActionSnapshot[],
    containerTypes: ContainerTypeDetailsByTypeId,
    nameOf: (action: ApiContainerActionSnapshot) => string,
): PortalContainerEntry[] {
    const isCollective = (action: ApiContainerActionSnapshot) =>
        containerTypes.get(action.container_type.foreign_key ?? '')?.usesIndividualManagement === false;

    const individual = actions
        .filter((action) => !isCollective(action))
        .map((action) => toIndividualEntry(action, nameOf));

    const collective = actions.filter(isCollective);

    return [...individual, ...toCollectiveEntries(collective, nameOf)].sort((one, other) =>
        compareForDisplay(one.label, other.label),
    );
}

function toIndividualEntry(
    action: ApiContainerActionSnapshot,
    nameOf: (action: ApiContainerActionSnapshot) => string,
): PortalIndividualContainerEntry {
    return {
        kind: 'individual',
        id: action.id,
        action: action.action,
        label: nameOf(action),
        containerNumber: action.container.display_string,
        remarks: action.remarks,
    };
}

function toCollectiveEntries(
    actions: readonly ApiContainerActionSnapshot[],
    nameOf: (action: ApiContainerActionSnapshot) => string,
): PortalCollectiveContainerEntry[] {
    const byTypeAndAction = new Map<string, PortalCollectiveContainerEntry>();

    for (const action of actions) {
        const key = `${action.container_type.foreign_key ?? ''}|${action.action}`;
        const entry = byTypeAndAction.get(key);

        if (entry === undefined) {
            byTypeAndAction.set(key, {
                kind: 'collective',
                id: key,
                action: action.action,
                label: nameOf(action),
                quantity: 1,
                remarks: action.remarks ? [action.remarks] : [],
            });
            continue;
        }

        entry.quantity += 1;
        if (action.remarks && !entry.remarks.includes(action.remarks)) {
            entry.remarks.push(action.remarks);
        }
    }

    return [...byTypeAndAction.values()];
}

/**
 * What to call the container type: the name the operator chose for the portal where they set one,
 * the name the snapshot carries otherwise. Mirrors `formatMaterialName` for services.
 */
function portalContainerTypeLabel(
    action: ApiContainerActionSnapshot,
    containerTypes: ContainerTypeDetailsByTypeId,
    isPortalSession: boolean,
): string {
    const externalLabel = isPortalSession
        ? containerTypes.get(action.container_type.foreign_key ?? '')?.externalLabel
        : undefined;

    return externalLabel || action.container_type.display_string;
}
